<template>
  <div
    id="app"
    class="font-Lato"
    :style="{ '--template-rows': templateRows, '--footer-row': footerRow }"
  >
    <navigation v-if="!$route.meta.navless" />
    <router-view />
    <template v-if="!$route.meta.footerless">
      <FooterMinimal v-if="minimalLayout" :dispatch="dispatch" />
      <template v-else-if="isMobile"><Footer :dispatch="dispatch"/></template>
      <template v-else><FooterDesktop :dispatch="dispatch"/></template>
    </template>
    <CookieBanner
      v-if="!hasDecided"
      :send="send"
      :cookieState="cookieState"
      :reopened="reopened"
    />
    <ScreenDark v-if="!hasDecided" />
    <ChangeCountryPopup v-if="popup" :dispatch="dispatch" />
  </div>
</template>

<script>
import Navigation from './layout/Navigation.vue';
import Footer from './layout/Footer.vue';
import FooterDesktop from './layout/FooterDesktop.vue';
import FooterMinimal from './layout/FooterMinimal.vue';
import CookieBanner from './components/shared/CookieBanner.vue';
import ScreenDark from './components/shared/ScreenDark.vue';
import shouldShowPopUp from '../utils/popup';
import { enableAnalytics, disableAnalytics } from './shared/analytics';
import {
  consentState,
  initConsent,
  setConsent,
  reopenConsent,
} from './shared/consent';
import { getItem, setItem } from './shared/storage';
import { LOCALSTORAGE_IDS } from './data/constants';
import {
  isMinimalLayoutEntity,
  specialCaseSpain,
} from './shared/entityHelpers';
import entity from '../region.json';

const ChangeCountryPopup = () =>
  import(
    /* webpackChunkName: "pop-up" */

    './components/ChangeCountryPopup.vue'
  );
export default {
  name: 'app',
  components: {
    Navigation,
    Footer,
    FooterDesktop,
    FooterMinimal,
    CookieBanner,
    ScreenDark,
    ChangeCountryPopup,
  },
  data() {
    return {
      isMobile: window.innerWidth <= 768,
      minimalLayout: isMinimalLayoutEntity(),
      // Presentation only. The decision itself lives in shared/consent.js so
      // that every component reacts to it instead of reading it once on mount.
      cookieState: {
        arrow: 'down',
        view: 'small',
        showMore: false,
      },
      // True when the banner was brought back from the footer rather than shown
      // on a first visit, which skips the one-second entrance delay.
      reopened: false,
      popup: null,
    };
  },
  computed: {
    templateRows() {
      return this.$route.meta.navless ? 'minmax(100vh, auto) auto' : null;
    },
    footerRow() {
      return this.$route.meta.navless ? '2' : null;
    },
    hasDecided() {
      return consentState.hasDecided;
    },
  },
  methods: {
    footerResize() {
      if (window.innerWidth <= 768 && this.isMobile === false) {
        this.isMobile = true;
      } else if (window.innerWidth > 768 && this.isMobile === true) {
        this.isMobile = false;
      }
    },
    checkLocalStorage() {
      // Runs in beforeMount, so it must not throw: every storage access here
      // goes through shared/storage.js.
      initConsent();

      const popupChoice = getItem(LOCALSTORAGE_IDS.CHANGE_COUNTRY_POPUP);
      if (popupChoice) {
        this.popup = false;
      } else {
        this.checkForUserLanguages();
      }
    },
    toggleMoreInfo() {
      // toggle more information text
      this.cookieState.showMore = !this.cookieState.showMore;
      // toggle height of cookie banner
      this.cookieState.view =
        this.cookieState.view === 'small' ? 'big' : 'small';
      // toggle arrow
      this.cookieState.arrow =
        this.cookieState.arrow === 'down' ? 'up' : 'down';
    },
    send(action) {
      switch (action.event) {
        // toggle button for more info
        case 'toggle':
          this.toggleMoreInfo();
          break;
        case 'accept':
          // user has accepted the privacy policy
          setConsent(true);
          this.reopened = false;
          this.connectWithGoogleAnalytics();
          break;
        case 'decline': {
          // user has declined the privacy policy. Stop analytics first, then
          // clear whatever had already been stored.
          const wasEnabled = consentState.allowCookies;
          if (wasEnabled) {
            disableAnalytics();
          }
          setConsent(false);
          this.reopened = false;
          // gtag.js cannot be un-injected once loaded, and a later config call
          // could re-set _ga, so only a reload fully removes it.
          if (wasEnabled) {
            window.location.reload();
          }
          break;
        }
        default:
      }
    },
    connectWithGoogleAnalytics() {
      if (consentState.allowCookies) {
        enableAnalytics().catch(() => {});
      }
    },
    dispatch(message) {
      if (message === 'cookieSettings') {
        // Withdrawal has to be as easy as consent: bring the banner back.
        this.reopened = true;
        reopenConsent();
        return;
      }
      if (message === 'changeCountry') {
        this.$router.push({ path: `/${this.$i18n.locale}/change-country` });
      } else if (message === 'open') {
        this.popup = true;
        return;
      }
      setItem(LOCALSTORAGE_IDS.CHANGE_COUNTRY_POPUP, true);
      this.popup = false;
    },
    checkForUserLanguages() {
      // remove once translations are ready for NL
      if (
        shouldShowPopUp() &&
        !specialCaseSpain(entity.entity, this.$i18n.locale)
      ) {
        this.popup = true;
      }
    },
  },
  beforeMount() {
    // Check if the user has already accepted or declined cookies
    // Needs to be done before mount to avoid flickering when removing cookies banner
    this.checkLocalStorage();
  },
  mounted() {
    // Must be run on mount after checking for localStorage in 'beforeMount' lifecycle
    this.connectWithGoogleAnalytics();
    window.addEventListener('resize', this.footerResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.footerResize);
  },
};
</script>

<style scoped>
#app {
  display: grid;
  grid-template-rows: var(--template-rows);
  --app-child-row: 2;
  --footer-row: 3;
  --template-rows: auto minmax(100vh, auto) auto;
}
#app > *:nth-child(2) {
  grid-row: var(--app-child-row);
}
</style>
